import { useEffect, useState } from 'react'

enum Step {
  Typing,
  Pausing,
  Deleting,
}

const DEFAULT_TYPING_INTERVAL = 150
const DEFAULT_PAUSING_MS = 1000
const DEFAULT_DELETING_TIME = 50

interface TypingAnimationOptions {
  typingInterval?: number
  pausingMs?: number
  deletingTime?: number
}

export default function useTypingAnimation(
  textList: Array<string>,
  disable?: boolean,
  options?: TypingAnimationOptions,
): string {
  const typingInterval = options?.typingInterval ?? DEFAULT_TYPING_INTERVAL
  const pausingMs = options?.pausingMs ?? DEFAULT_PAUSING_MS
  const deletingTime = options?.deletingTime ?? DEFAULT_DELETING_TIME
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [step, setStep] = useState(Step.Typing)
  const [typedText, setTypedText] = useState('')
  useEffect(() => {
    const currentText = textList[selectedIndex]
    if (currentText === undefined) {
      return
    }

    switch (step) {
      case Step.Typing: {
        const nextText = currentText.slice(0, typedText.length + 1)

        if (nextText === typedText) {
          setStep(Step.Pausing)
          return
        }

        const timeout = setTimeout(() => {
          setTypedText(nextText)
        }, typingInterval)
        return () => clearTimeout(timeout)
      }

      case Step.Deleting: {
        if (!typedText) {
          const nextIndex = selectedIndex + 1
          setSelectedIndex(textList[nextIndex] ? nextIndex : 0)
          setStep(Step.Typing)
          return
        }
        const nextRemaining = currentText.slice(0, typedText.length - 1)

        const timeout = setTimeout(() => {
          setTypedText(nextRemaining)
        }, deletingTime)

        return () => clearTimeout(timeout)
      }

      case Step.Pausing:
      default:
        if (disable) return

        const timeout = setTimeout(() => {
          setStep(Step.Deleting)
        }, pausingMs)
        return () => clearTimeout(timeout)
    }
  }, [typedText, textList, step, selectedIndex, disable, typingInterval, pausingMs, deletingTime])

  useEffect(() => {
    setSelectedIndex(0)
    setStep(Step.Typing)
    setTypedText('')
  }, [textList])

  return typedText
}
